import React, { FC, useEffect, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useParams } from 'react-router-dom'
import LoadingSquare from '../../../components/common/LoadingSquare'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MemberListModule from '../../../components/common/MemberListModule'
import SettingModal from '../../../components/common/SettingModal'
import { RequestPatchTaskCreateTaskByUrlSetting, TaskCreateTaskByUrlSettingMember } from '../../../../states/types/task'
import { fetchTaskCreateTaskByUrlSetting, updateTaskCreateTaskByUrlSetting } from '../../../../states/containers/task/taskSlice'
import { IconName } from '@fortawesome/fontawesome-common-types'
import { RootState } from '../../../../states/containers/store'

const STATUS_LIST = [
  {
    label: "作成する",
    value: true,
  },
  {
    label: "作成しない",
    value: false,
  },
]

const SELECT_LIST = [
  {
    label: "会社全体の設定に準拠する",
    value: "common",
  },
  {
    label: "作成する",
    value: "on",
  },
  {
    label: "作成しない",
    value: "off",
  },
]

const Index: FC = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [isCreateTaskByUrl, setIsCreateTaskByUrl] = useState<boolean>(false)

  const [originMemberList, setOriginMemberList] = useState<TaskCreateTaskByUrlSettingMember[]>([])
  const [commonValue, setCommonValue] = useState<boolean>(true)

  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false)
  const [targetMembers, setTargetMembers] = useState<TaskCreateTaskByUrlSettingMember[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const openUpdateModal = (memberIds: number[]) => {
    setTargetMembers(originMemberList.filter((v) => memberIds.includes(v.id)))
    setIsDisplayModal(true)
  }

  const closeModal = () => {
    setTargetMembers([])
    setIsDisplayModal(false)
  }

  const buildParam = (type: string) => {
    switch (type) {
      case "on":
        return true
      case "off":
        return false
      default:
        return null
    }
  }

  const buildParamToString = (type: boolean | null) => {
    if (type === null) {
      return "common"
    }
    if (type) return "on"
    return "off"
  }

  const changeCreateTaskByUrl = (data: {commonFlg: boolean, memberIds?: number[], type: string}) => {
    const param: RequestPatchTaskCreateTaskByUrlSetting = { change_common_setting: data.commonFlg }
    const create_task_by_url = buildParam(data.type)
    if (data.commonFlg) {
      param.create_task_by_url = create_task_by_url ?? true
    } else {
      param.member_ids = data.memberIds
      param.use_common_setting = create_task_by_url === null
      if (data.type !== "common") {
        param.create_task_by_url = create_task_by_url ?? true
      }
    }
    dispatch(updateTaskCreateTaskByUrlSetting(
      param,
      () => {
        closeModal()
        if (data.commonFlg) {
          setCommonValue(create_task_by_url ?? true)
          alert.success("会社全体の設定を変更しました")
        } else {
          setOriginMemberList((cur) => cur.map((v) => (
            param.member_ids?.includes(v.id)
              ? { ...v, use_common_setting: param.use_common_setting || false, create_task_by_url: param.create_task_by_url ?? null }
              : v
          )))
          alert.success("ユーザーの設定を変更しました")
        }
      },
    ))
  }

  useEffect(() => {
    setIsLoading(true)
    dispatch(fetchTaskCreateTaskByUrlSetting(
      (result) => {
        setOriginMemberList(result.members)
        setCommonValue(result.common.create_task_by_url)
        setIsCreateTaskByUrl(result.common.create_task_by_url)
        setIsLoading(false)
      },
    ))
  }, [])

  return (
    <section className="task_page">
      <Helmet>
        <title>
          {
            location.pathname === `/${language}${URL.SETTING_TASK_CREATE_TASK_BY_URL_SETTING.path}` ? (
              `Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - 業務仕分けボードの作業データをURLごとに作成する`
            ) : (
              `Qasee - 工数管理 - 業務仕分けボードの作業データをURLごとに作成する`
            )
          }
        </title>
      </Helmet>
      <section className="breadcrumb">
        {
          location.pathname === `/${language}${URL.SETTING_TASK_CREATE_TASK_BY_URL_SETTING.path}` ? (
            <>
              設定
              &nbsp;&gt;&nbsp;
              <Link
                to={setPath(URL.SETTING_COMMON.path, language)}
              >
                {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
              </Link>
            </>
          ) : (
            <>
              工数管理
            </>
          )
        }
        &nbsp;&gt;&nbsp;
        業務仕分けボードの作業データをURLごとに作成する
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          業務仕分けボードの作業データをURLごとに作成する
        </h1>
      </section>
      <section className="page_description">
        <p style={{ marginBottom: "4px" }}>業務仕分けボードの作業データをURLごとに作成するようにします。</p>
      </section>
      {
        isLoading ? (
          <LoadingSquare>
            <>
              設定を読み込んでいます…
            </>
          </LoadingSquare>
        ) : (
          <>
            {
              login.role_target_restriction || (
                <section className="page_contents content-area">
                  <div className="content-block">
                    <div className="block-header" style={{ display: "block" }}>
                      <div className="title" style={{ marginBottom: "16px" }}>
                        会社全体の設定
                      </div>
                    </div>
                    <div className="block-detail">
                      <div
                        className={`graphical_check_box${isCreateTaskByUrl ? " selected" : ""}`}
                        onClick={() => setIsCreateTaskByUrl(!isCreateTaskByUrl)}
                        role="none"
                      >
                        <FontAwesomeIcon
                          icon={['far', `${isCreateTaskByUrl ? 'check-square' : 'square'}` as IconName]}
                          className={`icon ${isCreateTaskByUrl ? "checked" : "no_check"}`}
                        />
                        <div>
                          <div className="check_box_title">
                            業務仕分けボードの作業データをURLごとに作成する
                          </div>
                          <div className="check_box_description">
                            業務仕分けボードの作業データをURLごとに作成するようにします
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="mt-4 button"
                        onClick={() => changeCreateTaskByUrl({ commonFlg: true, type: isCreateTaskByUrl ? "on" : "off" })}
                      >
                        設定する
                      </button>
                    </div>
                  </div>
                </section>
              )
            }
            <MemberListModule
              originMemberList={originMemberList.map((v) => ({ ...v, value: v.create_task_by_url }))}
              statusList={STATUS_LIST}
              commonValue={commonValue}
              valueLabel="業務仕分けボードの作業データをURLごとに作成する"
              type="bool"
              modalOpenCallback={(memberIds) => openUpdateModal(memberIds)}
            />
            <SettingModal
              targetMemberList={targetMembers.map((v) => ({ ...v, value: buildParamToString(v.create_task_by_url) }))}
              selectList={SELECT_LIST}
              label="業務仕分けボードの作業データをURLごとに作成する"
              prefix="業務仕分けボードの作業データをURLごとに"
              isDisplayModal={isDisplayModal}
              submitCallback={(data: { commonFlg: boolean; memberIds: number[]; type: string }) => changeCreateTaskByUrl(data)}
              closeCallback={closeModal}
            />
          </>
        )
      }
    </section>
  )
}

export default Index
